import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, Video, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Sample events for demonstration
  const sampleEvents = [
    {
      id: '1',
      title: 'CTC Cargo Network Annual Conference 2025',
      description: 'Join us for the largest gathering of Caribbean freight professionals. Network, learn, and grow your business.',
      event_type: 'conference',
      start_time: new Date(Date.now() + 30 * 86400000).toISOString(),
      end_time: new Date(Date.now() + 32 * 86400000).toISOString(),
      location: 'Miami Beach Convention Center, Florida',
      max_attendees: 500,
      registered_count: 312,
      registration_url: '#'
    },
    {
      id: '2',
      title: 'Webinar: Navigating Caribbean Customs Regulations',
      description: 'Expert panel discusses the latest customs requirements and best practices for smooth freight clearance.',
      event_type: 'webinar',
      start_time: new Date(Date.now() + 7 * 86400000).toISOString(),
      end_time: new Date(Date.now() + 7 * 86400000 + 3600000).toISOString(),
      location: 'Virtual',
      max_attendees: 200,
      registered_count: 89,
      registration_url: '#'
    },
    {
      id: '3',
      title: 'Caribbean Freight Tech Workshop',
      description: 'Hands-on workshop covering the latest technology solutions for freight management and tracking.',
      event_type: 'workshop',
      start_time: new Date(Date.now() + 14 * 86400000).toISOString(),
      end_time: new Date(Date.now() + 14 * 86400000 + 14400000).toISOString(),
      location: 'Kingston, Jamaica',
      max_attendees: 50,
      registered_count: 28,
      registration_url: '#'
    }
  ];

  const displayEvents = events.length > 0 ? events : sampleEvents;

  const getEventTypeStyle = (type) => {
    switch (type) {
      case 'conference': return 'bg-purple-100 text-purple-700';
      case 'webinar': return 'bg-blue-100 text-blue-700';
      case 'workshop': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-outfit font-bold text-ocean-900">Events & Webinars</h1>
            <p className="text-slate-600 mt-2">Connect and learn with Caribbean freight professionals</p>
          </div>

          {/* Featured Event */}
          {displayEvents.length > 0 && displayEvents[0].event_type === 'conference' && (
            <Card className="mb-8 overflow-hidden border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-white">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <Badge className={`mb-3 ${getEventTypeStyle(displayEvents[0].event_type)}`}>
                      Featured Event
                    </Badge>
                    <h2 className="text-2xl md:text-3xl font-outfit font-bold text-ocean-900 mb-3">
                      {displayEvents[0].title}
                    </h2>
                    <p className="text-slate-600 mb-4">{displayEvents[0].description}</p>
                    
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-purple-500" />
                        <div>
                          <p className="text-xs text-slate-500">Date</p>
                          <p className="font-medium">{new Date(displayEvents[0].start_time).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-purple-500" />
                        <div>
                          <p className="text-xs text-slate-500">Location</p>
                          <p className="font-medium">{displayEvents[0].location}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-purple-500" />
                        <div>
                          <p className="text-xs text-slate-500">Attendees</p>
                          <p className="font-medium">{displayEvents[0].registered_count} / {displayEvents[0].max_attendees}</p>
                        </div>
                      </div>
                    </div>

                    <Button className="bg-purple-600 hover:bg-purple-700">
                      Register Now <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                  <div className="hidden md:block">
                    <div className="w-48 h-48 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Calendar className="h-24 w-24 text-purple-300" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Events List */}
          <div className="space-y-4">
            {displayEvents.slice(1).map((event) => (
              <Card key={event.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-shrink-0 text-center md:text-left">
                      <div className="w-16 h-16 bg-slate-100 rounded-lg flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-cyan-500">
                          {new Date(event.start_time).getDate()}
                        </span>
                        <span className="text-xs text-slate-500 uppercase">
                          {new Date(event.start_time).toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getEventTypeStyle(event.event_type)}>
                          {event.event_type === 'webinar' && <Video className="h-3 w-3 mr-1" />}
                          {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                        </Badge>
                        {event.location === 'Virtual' && (
                          <Badge variant="outline" className="text-xs">Online</Badge>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-ocean-900 mb-2">{event.title}</h3>
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">{event.description}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>
                            {new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          <span>{event.location}</span>
                        </div>
                        {event.max_attendees && (
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span>{event.registered_count} / {event.max_attendees} registered</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <Button className="bg-cyan-500 hover:bg-cyan-600 w-full md:w-auto">
                        Register
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {events.length === 0 && (
            <div className="text-center mt-8 text-slate-500">
              <p className="text-sm">These are sample events. Real events will appear as they're scheduled.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EventsList;
